import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  LOCALE_BCP47,
  translate,
  type UiLocale,
} from '../i18n/uiMessages';

const STORAGE_KEY = 'hawk-ui-locale';

function readStoredLocale(): UiLocale {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    if (s === 'de' || s === 'en' || s === 'vi' || s === 'fr') return s;
  } catch {
    /* ignore */
  }
  return 'en';
}

export type UiLocaleContextValue = {
  locale: UiLocale;
  setLocale: (locale: UiLocale) => void;
  /** BCP 47 tag for `toLocaleString` / `toLocaleTimeString` */
  localeTag: string;
  t: (key: string, vars?: Record<string, string | number>) => string;
};

const UiLocaleContext = createContext<UiLocaleContextValue | null>(null);

export function UiLocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<UiLocale>(() => readStoredLocale());

  const setLocale = useCallback((next: UiLocale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => translate(locale, key, vars),
    [locale],
  );

  const localeTag = LOCALE_BCP47[locale];

  const value = useMemo(
    () => ({ locale, setLocale, localeTag, t }),
    [locale, setLocale, localeTag, t],
  );

  return <UiLocaleContext.Provider value={value}>{children}</UiLocaleContext.Provider>;
}

export function useUiLocale(): UiLocaleContextValue {
  const ctx = useContext(UiLocaleContext);
  if (!ctx) {
    throw new Error('useUiLocale must be used within UiLocaleProvider');
  }
  return ctx;
}
